import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SlantedReactangle from "@tecumseh/Icons/SlantedReactangle";
import CustomApplicationState from "@tecumseh/Store/Data/CustomApplicationState/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => ({
    categories: state.TECData.customCategoriesById,
});

interface Props extends WidgetProps, HasCategoryContext {}

export interface CategoryDetailBannerStyles {
    bannerContainer?: InjectableCss;
    bannerTitle?: TypographyProps;
    bannerDescription?: InjectableCss;
    parent?: InjectableCss;
    childOne?: InjectableCss;
    childTwo?: InjectableCss;
    bannerTextContainer?: InjectableCss;
    slantedLineIcon?: InjectableCss;
    slantlinetext?: InjectableCss;
}

export const bannerStyles: CategoryDetailBannerStyles = {
    bannerContainer: {
        css: css`
            border-radius: 8px;
            max-width: 765px;
            position: relative;
            top: 222px;
            width: 100%;
            margin: auto;
            padding: 33px 0;
            border: 0.5px solid rgba(0, 0, 0, 0.5);
            border-top: 6px solid ${getColor("primary")} !important;
            box-shadow: 0px 2px 32px rgba(67, 67, 67, 0.5);
            opacity: 1;
            background: linear-gradient(90deg, rgba(255, 255, 255, 0.751109) 0%, rgba(255, 255, 255, 0.500403) 100%);
            backdrop-filter: blur(20px);

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 340px !important;
                        height: auto !important;
                        left: 0;
                        margin: auto;
                        top: 150px;
                        box-shadow: 0px 2px 32px rgba(67, 67, 67, 0.164909);
                    `,
                    css`
                        max-width: 400px !important;
                        height: auto !important;
                        left: 0;
                        margin: auto;
                        top: 150px;
                        box-shadow: 0px 2px 32px rgba(67, 67, 67, 0.164909);
                    `,
                    css`
                        max-width: 513px !important;
                        height: auto !important;
                        left: 0;
                        margin: auto;
                        top: 180px;
                        box-shadow: 0px 2px 32px rgba(67, 67, 67, 0.5);
                    `,
                    null,
                    null,
                ])}
        `,
    },
    bannerTextContainer: {
        css: css`
            padding-left: 38.5px;
            padding-right: 38.5px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        padding-right: 17.5px;
                        padding-left: 17.5px;
                    `,
                    css`
                        width: 100%;
                        padding-right: 17.5px;
                        padding-left: 17.5px;
                    `,
                    css`
                        width: 100%;
                        padding-right: 17.5px;
                        padding-left: 17.5px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    bannerTitle: {
        variant: "h1",
        css: css`
            opacity: 1;
            color: ${getColor("secondary")};
            font-family: ${TYPOGRAPHY.fontFamily.barlow};
            font-weight: ${TYPOGRAPHY.weight.light};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
            text-align: left;
            line-height: ${TYPOGRAPHY.lineHeight.xlarger};
            text-transform: uppercase;
            word-break: break-word;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: ${TYPOGRAPHY.size.xbase};
                        `,
                        css`
                            margin-top: 32px;
                        `,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    bannerDescription: {
        css: css`
            margin-top: 10px;
            opacity: 1;
            color: ${COLORS.secondaryNavyThree};
            font-family: ${TYPOGRAPHY.fontFamily.montserrat};
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.light};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
            text-align: left;
            line-height: ${TYPOGRAPHY.lineHeight.smallbase};
            background: none;
            div,
            p,
            span,
            li,
            ol,
            ul,
            section,
            main,
            body {
                opacity: 1;
                color: ${COLORS.secondaryNavyThree};
                font-family: ${TYPOGRAPHY.fontFamily.montserrat};
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.light};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
                text-align: left;
                line-height: ${TYPOGRAPHY.lineHeight.smallbase};
                background: none;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: ${TYPOGRAPHY.size.smaller};
                                line-height: ${TYPOGRAPHY.lineHeight.smallestbase};
                            `,
                            css`
                                font-size: ${TYPOGRAPHY.size.smaller};
                                line-height: ${TYPOGRAPHY.lineHeight.smallestbase};
                            `,
                            css`
                                font-size: ${TYPOGRAPHY.size.smaller};
                                line-height: ${TYPOGRAPHY.lineHeight.smallestbase};
                            `,
                            null,
                        ],
                        "minMax",
                    )}
            }
        `,
    },
    slantedLineIcon: {
        css: css`
            margin-left: 10px;
            > svg {
                color: ${getColor("primary")};
                height: 35px;
                width: 35px;
            }
        `,
    },
    slantlinetext: {
        css: css`
            display: flex;
        `,
    },
};

const styles = bannerStyles;

const CategoryDetailBanner = ({ category, categories }: Props & ReturnType<typeof mapStateToProps>) => {
    if (!category) {
        return null;
    }
    const data = categories.byId[category.id];

    const [height, setHeight] = React.useState(0);
    const ref = React.useRef<HTMLInputElement>(null);

    const backgroundStyle = `background-image: url(${category.properties.imagePath});`;

    React.useEffect(() => {
        if (ref.current !== null) {
            setHeight(ref.current.offsetHeight);
        }
    }, [category]);

    const bannerWrapper = {
        css: css`
            max-width: 100%;
            min-height: 366px !important;
            ${backgroundStyle};
            background-size: 100%;
            height: 100%;
            position: relative;
            margin-bottom: 70px;
            background-position: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        ${backgroundStyle}
                        background-size:cover;
                        margin-bottom: 40px;
                    `,
                    css`
                        ${backgroundStyle}
                        background-size:cover;
                        margin-bottom: 40px;
                    `,
                    css`
                        margin-bottom: 50px;
                    `,
                    null,
                    null,
                ])}
        `,
    };
    const bannerMarginWrapper = {
        css: css`
            max-width: 100%;
            min-height: 366px !important;
            ${backgroundStyle};
            background-size: 100%;
            height: 100%;
            position: relative;
            margin-bottom: calc(70px + ${height / 2}px);
            background-position: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: calc(10px + ${height / 2}px);
                        ${backgroundStyle}
                        background-size:cover;
                    `,
                    css`
                        margin-bottom: calc(10px + ${height / 2}px);
                        ${backgroundStyle}
                        background-size:cover;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    };

    return (
        <>
            <StyledWrapper
                className="bannerHeight"
                {...(height < 208 ? { ...bannerWrapper } : { ...bannerMarginWrapper })}
            >
                <StyledWrapper
                    css={css`
                        max-width: 1200px;
                        margin: auto;
                    `}
                >
                    <StyledWrapper ref={ref} {...styles.bannerContainer}>
                        <StyledWrapper {...styles.bannerTextContainer}>
                            <StyledWrapper {...styles.slantlinetext}>
                                <Typography as="h1" {...styles.bannerTitle}>
                                    {data?.properties?.extendedTitle ? translate(data?.properties?.extendedTitle) : category.shortDescription}
                                </Typography>
                                <Icon src={SlantedReactangle} size={35} {...styles.slantedLineIcon}></Icon>
                            </StyledWrapper>
                            <StyledWrapper>
                                <Typography as="p" {...styles.bannerDescription}>
                                    {" "}
                                    {parse(data?.properties?.description, parserOptions)}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(CategoryDetailBanner)),
    definition: {
        group: "Category Details",
        icon: "image-landscape",
        allowedContexts: ["CategoryDetailsPage", "ProductListPage"],
    },
};

export default widgetModule;
